import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { createPortal } from "react-dom";

export default function DocumentPreview({ document: preview, onClose }) {
  const [objectUrl, setObjectUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!preview?.url) return undefined;
    let active = true;
    let generatedUrl = "";
    setObjectUrl("");
    setError("");
    fetch(preview.url, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 401 ? "Sessione non autorizzata" : "Documento non disponibile");
        const source = await response.blob();
        const extension = (preview.fileName || "").split(".").pop()?.toLowerCase();
        const forcedType = extension === "pdf" ? "application/pdf"
          : (["jpg", "jpeg"].includes(extension) ? "image/jpeg"
          : (extension === "png" ? "image/png" : source.type));
        const blob = forcedType && forcedType !== source.type
          ? new Blob([await source.arrayBuffer()], { type: forcedType })
          : source;
        generatedUrl = URL.createObjectURL(blob);
        if (active) setObjectUrl(generatedUrl);
        else URL.revokeObjectURL(generatedUrl);
      })
      .catch((reason) => active && setError(reason?.message || "Impossibile aprire l'anteprima"));
    return () => {
      active = false;
      if (generatedUrl) URL.revokeObjectURL(generatedUrl);
    };
  }, [preview]);

  useEffect(() => {
    if (!preview) return undefined;
    const oldOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose?.(); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [preview, onClose]);

  if (!preview) return null;
  const separator = preview.url.includes("?") ? "&" : "?";
  const downloadUrl = `${preview.url}${separator}download=1&filename=${encodeURIComponent(preview.fileName || "documento.pdf")}`;
  const extension = (preview.fileName || "").split(".").pop()?.toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(extension);
  const canPreview = extension === "pdf" || isImage;
  return createPortal(<div className="fixed inset-0 flex bg-black/80 p-0 sm:p-5" style={{zIndex:9999}} role="presentation" onClick={onClose}>
    <div className="document-preview-dialog mx-auto flex h-[100dvh] w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl sm:h-full sm:rounded-2xl" role="dialog" aria-modal="true" onClick={(event)=>event.stopPropagation()}>
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2 sm:px-5 sm:py-3">
        <p className="min-w-0 truncate font-heading font-semibold text-[#3D405B]">{preview.title || preview.fileName}</p>
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <a href={downloadUrl} download={preview.fileName || "documento"} className="btn-sage !px-3 !py-2 text-xs"><Download className="w-4 h-4"/><span className="hidden sm:inline">Scarica</span></a>
          <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full border text-red-500" aria-label="Chiudi anteprima"><X className="w-5 h-5"/></button>
        </div>
      </div>
      <div className="relative h-full min-h-0 w-full bg-[#edf1f4]">
        {!objectUrl && !error && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#607086]"><Loader2 className="h-8 w-8 animate-spin"/><p>Caricamento anteprima…</p></div>}
        {error && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center"><p className="font-semibold text-red-600">{error}</p><p className="text-sm text-[#607086]">Chiudi l’anteprima e riprova. Se il file non è PDF, puoi scaricarlo con il pulsante in alto.</p></div>}
        {objectUrl && canPreview && (isImage
          ? <div className="flex h-full w-full items-center justify-center overflow-auto p-2 sm:p-4"><img src={objectUrl} alt={preview.title || preview.fileName} className="max-h-full max-w-full object-contain"/></div>
          : <iframe title={preview.title || "Anteprima documento"} src={objectUrl} className="h-full min-h-0 w-full border-0 bg-[#edf1f4]" />)}
        {objectUrl && !canPreview && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center"><p className="font-semibold text-[#3D405B]">Anteprima non disponibile per questo formato.</p><p className="text-sm text-[#607086]">Puoi scaricare il file mantenendo il nome originale con il pulsante in alto.</p></div>}
      </div>
    </div>
  </div>, window.document.body);
}
